import React, {useCallback, useEffect} from 'react';
import {Col, Form} from 'react-bootstrap';
import {useField, useFormikContext} from 'formik';
import {Drawer, DrawerKey} from 'components/customer/Product/entity/Drawer';
import {useAvailableDrawerSystems} from 'components/customer/Product/Drawer/helpers/useAvailableDrawerSystems';
import SelectField from 'shared/components/SelectField';
import {FormGroup} from 'shared/components/Forms/FormGroup';
import {DrawerSystem} from 'shared/types/DrawerSystem';

interface DrawerTypeProps {
    isDrawerComponent?: boolean;
    isInnerDrawer?: boolean;
    runnerIndex?: number;
    fieldName: string;
    labelColSpan?: number;
}

export const DrawerType = ({
    runnerIndex,
    fieldName,
    isDrawerComponent = false,
    isInnerDrawer = false,
    labelColSpan = 5,
}: DrawerTypeProps) => {
    const {values, setFieldValue} = useFormikContext<{
        drawers: Drawer[];
        cabinet_include_hardware: number;
        [key: string]: number | Drawer[];
    }>();
    const [{value: drawerType}, , {setValue}] = useField<number>({
        name: `drawers[${runnerIndex}].${fieldName}`,
    });

    const availableDrawerSystems = useAvailableDrawerSystems(
        isInnerDrawer,
        isDrawerComponent
    );

    const selectHandler = (
        name: string,
        value: number,
        drawerSystem?: DrawerSystem
    ) => {
        if (isDrawerComponent) {
            void setFieldValue(name, value);
        } else {
            if (isInnerDrawer) {
                const drawer = values.drawers[Number(runnerIndex)];

                void setFieldValue(`drawers[${runnerIndex}]`, {
                    ...drawer,
                    [name]: value,
                    systemTopClearance: drawerSystem.topClearance,
                    systemBottomClearance: drawerSystem.bottomClearance,
                    systemInnerDrawerBottomClearance:
                        drawerSystem.innerDrawerBottomClearance,
                });
            } else {
                void setFieldValue(`drawers[${runnerIndex}].${name}`, value);
            }
        }
    };

    const updatePosition = useCallback(
        (value: number) => {
            if (!isInnerDrawer) return;

            const currentDrawerType = availableDrawerSystems.find(
                (system) => system.id == drawerType
            );

            if (currentDrawerType) {
                const selectedSystem = availableDrawerSystems.find(
                    (system) => system.id == value
                );

                if (selectedSystem) {
                    const drawer = values.drawers[Number(runnerIndex)];
                    if (drawer) {
                        const newPosition =
                            drawer.position_to_bottom -
                            currentDrawerType.bottomClearance +
                            selectedSystem.bottomClearance;

                        void setFieldValue(`drawers[${runnerIndex}]`, {
                            ...drawer,
                            position_to_bottom: newPosition,
                            systemTopClearance: selectedSystem.topClearance,
                            systemBottomClearance:
                                selectedSystem.bottomClearance,
                            systemInnerDrawerBottomClearance:
                                selectedSystem.innerDrawerBottomClearance,
                        });
                    }
                }
            }
        },
        [
            runnerIndex,
            fieldName,
            isInnerDrawer,
            availableDrawerSystems,
            drawerType,
            values.drawers,
        ]
    );

    const customOnChange = useCallback(
        (name: string, value: string) => {
            updatePosition(Number(value));
            void setValue(Number(value));
        },
        [setValue, updatePosition]
    );

    const customOnChangeDrawerComponent = useCallback(
        (name: string, value: string) => {
            void setFieldValue(name, value);
            void setFieldValue(`drawers[0].insert_id`, value);
        },
        [setFieldValue]
    );

    useEffect(() => {
        if (isDrawerComponent) {
            void selectHandler(
                fieldName,
                Number(values[fieldName as DrawerKey])
            );
        }
        // Not sure what this code is supposed to do
        // else {
        //     const drawer = values.drawers[Number(runnerIndex)];
        //     if (drawer) {
        //         const drawerValue = drawer[fieldName as DrawerKey];
        //         void selectHandler(fieldName, Number(drawerValue));
        //     }
        // }
    }, [values.cabinet_include_hardware]);

    useEffect(() => {
        if (
            availableDrawerSystems?.length &&
            values.drawers &&
            fieldName == 'insert_id'
        ) {
            const drawer = values.drawers[Number(runnerIndex)];

            if (drawer) {
                const selectedType = availableDrawerSystems.find(
                    (system) => system.id == drawer[fieldName as DrawerKey]
                );

                if (
                    selectedType == undefined ||
                    !drawer.hasOwnProperty('systemTopClearance') ||
                    !drawer.hasOwnProperty('systemBottomClearance') ||
                    !drawer.hasOwnProperty('systemInnerDrawerBottomClearance')
                ) {
                    if (selectedType != undefined) {
                        void selectHandler(
                            fieldName,
                            selectedType.id,
                            selectedType
                        );
                    } else {
                        void selectHandler(
                            fieldName,
                            availableDrawerSystems[0].id,
                            availableDrawerSystems[0]
                        );
                    }
                }
            }
        }
    }, [availableDrawerSystems, values.drawers]);

    return (
        <FormGroup>
            <Form.Label column md={labelColSpan} xs={12} style={{zIndex: 1}}>
                Type
                <span>*</span>
            </Form.Label>
            <Col md={12 - labelColSpan} xs={12}>
                <SelectField
                    name={
                        isDrawerComponent
                            ? fieldName
                            : `drawers[${runnerIndex}].${fieldName}`
                    }
                    customOnChange={
                        isDrawerComponent
                            ? customOnChangeDrawerComponent
                            : customOnChange
                    }
                    options={availableDrawerSystems?.map(
                        ({id, name, disabled}) => ({
                            label: name,
                            value: String(id),
                            disabled,
                        })
                    )}
                />
            </Col>
        </FormGroup>
    );
};
